/**
 * useMerchantProfile Hook - Aggregator Pattern Implementation
 *
 * This hook aggregates merchant profile data:
 * - Fetches merchant profile information by ID
 * - Provides computed properties for display
 * - Handles profile completeness checks
 *
 * Pattern: AGGREGATOR
 * - Fetches and aggregates merchant profile data
 * - Provides computed display values
 * - Single loading state for UI
 * - Read-only data aggregation
 *
 * IMPORTANT: This hook provides profile data only.
 * For profile update actions, use useMerchantProfileActions coordinator hook.
 */

import { useGetMerchantProfile } from '@/generated/hooks';
import type { GetMerchantProfileResponseType } from '@zlinkx/shared-v2';
import type { AggregatorPattern } from '../../types/hook-patterns.types';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Options for useMerchantProfile hook
 */
interface UseMerchantProfileOptions {
	/**
	 * Merchant ID
	 */
	merchantId: number;
	/**
	 * Enable/disable fetching
	 */
	enabled?: boolean;
}

/**
 * Raw merchant profile data extracted from API response
 */
interface MerchantProfileData {
	profile: GetMerchantProfileResponseType['data'] | null;
	merchantId: number | null;
	merchantName: string;
	merchantDescription: string | null;
	logoUrl: string | null;
	slug: string | null;
}

/**
 * Computed merchant profile values for UI display
 */
interface MerchantProfileComputed {
	// Status flags
	hasProfile: boolean;
	hasLogo: boolean;
	hasDescription: boolean;
	hasSlug: boolean;
	isProfileComplete: boolean;

	// Formatted displays
	displayName: string;
	registrationLink: string | null;
}

/**
 * Return type for useMerchantProfile hook
 */
type UseMerchantProfileReturn = AggregatorPattern<
	MerchantProfileData,
	MerchantProfileComputed
>;

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

/**
 * Aggregates merchant profile data with computed properties
 *
 * @example
 * ```tsx
 * function MerchantSettingsPage({ merchantId }: Props) {
 *   const merchantProfile = useMerchantProfile({
 *     merchantId,
 *   });
 *
 *   if (merchantProfile.isLoading) {
 *     return <LoadingSpinner />;
 *   }
 *
 *   if (merchantProfile.error) {
 *     return <ErrorMessage message={merchantProfile.error} />;
 *   }
 *
 *   const { data, computed } = merchantProfile;
 *
 *   return (
 *     <div>
 *       <h1>{computed.displayName}</h1>
 *       {computed.hasLogo && <img src={data.logoUrl} />}
 *       {computed.registrationLink && (
 *         <CopyableLink link={computed.registrationLink} />
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function useMerchantProfile(
	options: UseMerchantProfileOptions,
): UseMerchantProfileReturn {
	const { merchantId, enabled = true } = options;

	// ========================================
	// Data Fetching
	// ========================================

	const profileQuery = useGetMerchantProfile(
		{ merchantId },
		{
			enabled: enabled && !!merchantId,
		},
	);

	// ========================================
	// Data Processing
	// ========================================

	const profile: GetMerchantProfileResponseType['data'] | undefined = profileQuery.data;

	const data: MerchantProfileData = {
		profile: profile || null,
		merchantId: profile?.merchant_id ?? null,
		merchantName: profile?.merchant_name || '',
		merchantDescription: profile?.merchant_description || null,
		logoUrl: profile?.logo_url || null,
		slug: profile?.slug || null,
	};

	// ========================================
	// Computed Values
	// ========================================

	const customerAppUrl = (() => {
		const envValue = (import.meta.env as Record<string, unknown>).VITE_CUSTOMER_APP_URL;
		return typeof envValue === 'string' && envValue.length > 0 ? envValue : 'https://zlinkx.app';
	})();

	const computed: MerchantProfileComputed = {
		// Status flags
		hasProfile: !!profile,
		hasLogo: !!data.logoUrl,
		hasDescription: !!data.merchantDescription,
		hasSlug: !!data.slug,
		isProfileComplete: !!data.merchantName && !!data.merchantDescription,

		// Formatted displays
		displayName: data.merchantName || 'Unnamed Merchant',
		registrationLink: data.slug ? `${customerAppUrl}/join/${data.slug}` : null,
	};

	// ========================================
	// Return Aggregator Pattern
	// ========================================

	return {
		data,
		computed,
		isLoading: profileQuery.isLoading,
		error: profileQuery.error ? String(profileQuery.error.message || 'Failed to load merchant profile') : null,
	};
}

// ============================================================================
// EXPORTS
// ============================================================================

export default useMerchantProfile;

export type {
	UseMerchantProfileOptions,
	MerchantProfileData,
	MerchantProfileComputed,
	UseMerchantProfileReturn,
};
